import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Edit3, Trash2, Search, Bike } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function ClientesPage() {
    const [clientes, setClientes] = useState([]);
    const [motos, setMotos] = useState([]);
    const [servicos, setServicos] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ nome: "", morada: "", nif: "", telefone: "", email: "" });
    const [saving, setSaving] = useState(false);

    async function load() {
        const [cl, mt, sv] = await Promise.all([api.get("/clientes"), api.get("/motos"), api.get("/servicos")]);
        setClientes(cl.data);
        setMotos(mt.data);
        setServicos(sv.data);
    }
    useEffect(() => {
        load();
    }, []);

    function openNew() {
        setEditing(null);
        setForm({ nome: "", morada: "", nif: "", telefone: "", email: "" });
        setOpen(true);
    }
    function openEdit(c) {
        setEditing(c);
        setForm({ nome: c.nome, morada: c.morada, nif: c.nif, telefone: c.telefone, email: c.email });
        setOpen(true);
    }

    async function save() {
        if (!form.nome.trim()) {
            toast.error("Nome obrigatório");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/clientes/${editing.id}`, form);
                toast.success("Cliente atualizado.");
            } else {
                await api.post("/clientes", form);
                toast.success("Cliente criado.");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        } finally {
            setSaving(false);
        }
    }

    async function remove(c) {
        const nServicos = servicosCountByCliente[c.id] || 0;
        if (nServicos > 0) {
            toast.error(`Cliente tem ${nServicos} serviço(s) no histórico e não pode ser eliminado.`);
            return;
        }
        if (!window.confirm(`Eliminar o cliente "${c.nome}"? Esta ação remove também as motos associadas.`)) return;
        try {
            await api.delete(`/clientes/${c.id}`);
            toast.success("Cliente eliminado.");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }

    const filtered = clientes.filter((c) =>
        [c.nome, c.email, c.telefone, c.nif].join(" ").toLowerCase().includes(q.toLowerCase())
    );
    const motosCountByCliente = motos.reduce((acc, m) => {
        acc[m.cliente_id] = (acc[m.cliente_id] || 0) + 1;
        return acc;
    }, {});
    const servicosCountByCliente = servicos.reduce((acc, s) => {
        acc[s.cliente_id] = (acc[s.cliente_id] || 0) + 1;
        return acc;
    }, {});

    return (
        <>
            <PageHeader
                eyebrow="Registo"
                title="Clientes"
                description="Proprietários registados na oficina."
                testid="clientes-header"
                actions={
                    <Button onClick={openNew} className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]" data-testid="new-cliente-btn">
                        <Plus className="mr-2 h-4 w-4" /> Novo cliente
                    </Button>
                }
            />

            <div className="mb-4 flex max-w-md items-center gap-2 border border-zinc-200 bg-white px-3">
                <Search className="h-4 w-4 text-zinc-400" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Procurar por nome, NIF, email..."
                    className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
                    data-testid="clientes-search-input"
                />
            </div>

            <div className="border border-zinc-200 bg-white" data-testid="clientes-table">
                <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                    <div className="col-span-3">Nome</div>
                    <div className="col-span-2">NIF</div>
                    <div className="col-span-2">Telefone</div>
                    <div className="col-span-3">Email</div>
                    <div className="col-span-1 text-center">Motos</div>
                    <div className="col-span-1 text-right">Ações</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-zinc-500">
                        Sem clientes registados.
                    </div>
                ) : (
                    filtered.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`cliente-row-${c.id}`}
                            className="grid grid-cols-12 items-center gap-2 border-b border-zinc-100 px-4 py-3 text-sm hover:bg-zinc-50"
                        >
                            <div className="col-span-3 font-medium text-zinc-900">
                                {c.nome}
                                {c.morada ? (
                                    <div className="text-xs text-zinc-500 truncate">{c.morada}</div>
                                ) : null}
                            </div>
                            <div className="col-span-2 font-mono text-xs text-zinc-700">{c.nif || "—"}</div>
                            <div className="col-span-2 text-zinc-700">{c.telefone || "—"}</div>
                            <div className="col-span-3 truncate text-zinc-700">{c.email || "—"}</div>
                            <div className="col-span-1 text-center text-zinc-700">{motosCountByCliente[c.id] || 0}</div>
                            <div className="col-span-1 flex justify-end gap-1">
                                <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7 rounded-sm"
                                    onClick={() => openEdit(c)}
                                    data-testid={`cliente-edit-${c.id}`}
                                >
                                    <Edit3 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7 rounded-sm text-red-600 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                    onClick={() => remove(c)}
                                    disabled={(servicosCountByCliente[c.id] || 0) > 0}
                                    title={(servicosCountByCliente[c.id] || 0) > 0 ? "Cliente tem histórico e não pode ser eliminado" : "Eliminar cliente"}
                                    data-testid={`cliente-delete-${c.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm" data-testid="cliente-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit']">
                            {editing ? "Editar cliente" : "Novo cliente"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Nome *
                            </Label>
                            <Input
                                value={form.nome}
                                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="cliente-nome-input"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Morada
                            </Label>
                            <Input
                                value={form.morada}
                                onChange={(e) => setForm({ ...form, morada: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="cliente-morada-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                NIF
                            </Label>
                            <Input
                                value={form.nif}
                                onChange={(e) => setForm({ ...form, nif: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="cliente-nif-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Telefone
                            </Label>
                            <Input
                                value={form.telefone}
                                onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="cliente-telefone-input"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Email
                            </Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="cliente-email-input"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            data-testid="cliente-save-btn"
                            className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        >
                            {saving ? "A guardar..." : "Guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
